import { listGroups, listInvitations, useListWorkspaces } from '@modusign/openapi-client-user';
import {
  GroupResponse,
  ListGroupsParams,
  ListInvitationsParams,
  WorkspaceResponseSeat,
} from '@modusign/openapi-client-user/model';
import { useQuery } from 'react-query';
import { useSelector } from 'react-redux';

import { useWorkspaceId } from '@app/hooks/useWorkspaceId';
import authService from '@app/libs/auth/authService';
import documentAuthService from '@app/libs/auth/documentAuthService';
import getAllList from '@app/libs/getAllList';
import { logger } from '@app/libs/logger';
import findActiveWorkspace from '@app/libs/workspaceService/findActiveWorkspace';
import { getName } from '@app/store/user/user/selectors';

/**
 * @deprecated 이 파일의 다른 훅에서 사용하기 위한 훅입니다. 직접 사용하지 마세요.
 */
export function useWorkspaceListApi() {
  return useListWorkspaces({
    query: {
      staleTime: 300,
      enabled: authService.hasAccessToken(),
    },
  });
}

export function useGroupListApi(param?: ListGroupsParams) {
  const workspaceId = useWorkspaceId();
  return useQuery<GroupResponse[], Error>({
    queryKey: ['groups', workspaceId, param],
    queryFn: () =>
      getAllList({
        queryFn: (offset, limit) => {
          return listGroups(workspaceId, { ...param, limit, offset }).then(({ count, groups }) => ({
            count,
            list: groups,
          }));
        },
      }),
    staleTime: 5 * 60 * 1000,
    enabled: !!workspaceId && !documentAuthService.isLinkUser(),
  });
}

function useCurrentWorkspaceApi() {
  const workspaceId = useWorkspaceId();
  const { data } = useWorkspaceListApi();
  return data?.workspaces.find((ws) => ws.id === workspaceId);
}

export function useInvitationListApi(params: Omit<ListInvitationsParams, 'limit' | 'offset'>) {
  const workspaceId = useWorkspaceId();
  return useQuery({
    queryKey: ['invitations', workspaceId, params],
    queryFn: () =>
      getAllList({
        queryFn: async (offset, limit) => {
          const { count, invitations } = await listInvitations(workspaceId, {
            limit,
            offset,
            ...params,
          });
          return {
            count,
            list: invitations,
          };
        },
      }),
    staleTime: 300,
  });
}

export function useWorkspace(): {
  id: string;
  type: 'PUBLIC' | 'PRIVATE';
  active: boolean;
  name: string | null;
  seat: WorkspaceResponseSeat;
  createdAt: string;
} {
  const workspaceId = useWorkspaceId();
  const { data, isSuccess } = useWorkspaceListApi();

  const userName = useSelector(getName);

  const currentWorkspace = data?.workspaces.find((ws) => ws.id === workspaceId);
  const activeWorkspace = findActiveWorkspace(data?.workspaces || []);

  if (isSuccess && workspaceId && !currentWorkspace) {
    logger.error(`Invalid workspace id set. User isn't a member of workspace ${workspaceId}`, {
      workspaceId,
    });
  }

  if (currentWorkspace) {
    return {
      id: currentWorkspace.id,
      active: currentWorkspace === activeWorkspace,
      name: currentWorkspace.name || `${userName}의 워크스페이스`,
      seat: currentWorkspace.seat,
      type: currentWorkspace.type,
      createdAt: currentWorkspace.createdAt,
    };
  }

  return {
    id: '',
    active: true,
    name: '',
    type: 'PRIVATE',
    seat: { seatLimit: 0 },
    createdAt: '',
  };
}

export function useCurrentUserMemberId(): string {
  const data = useCurrentWorkspaceApi();

  return data?.member.id || '';
}

export function useCurrentUserName(): string {
  const data = useCurrentWorkspaceApi();

  return data?.member.userName || '';
}

export function useCurrentUserRole(): {
  // TODO: 역할 관련 컴포넌트 구현할 때 도메인 타입 정의하고 교체하기
  id: string;
  workspaceId: string;
  type: 'ADMIN' | 'CUSTOM';
  name: string;
  description: string | null;
  createdAt: string;
  updatedAt: string;
} | null {
  const data = useCurrentWorkspaceApi();

  return data?.role || null;
}

export function useCurrentMemberGroupList() {
  const data = useCurrentWorkspaceApi();

  return data?.groups || [];
}

export function useGroupList() {
  const { data } = useGroupListApi();

  return data || [];
}

export function useHasPublicWorkspace(): boolean {
  const { data } = useWorkspaceListApi();

  return data?.workspaces.some((ws) => ws.type === 'PUBLIC') || false;
}

export function useIsPublicWorkspace() {
  return useWorkspace().type === 'PUBLIC';
}

export function useOtherWorkspace(): {
  id: string;
  name: string | null;
  type: 'PRIVATE' | 'PUBLIC';
  isPublic: boolean;
  isPrivate: boolean;
} | null {
  const currentWorkspaceId = useWorkspaceId();
  const { data } = useWorkspaceListApi();

  const otherWorkspace = data?.workspaces.find((ws) => ws.id !== currentWorkspaceId);

  if (!otherWorkspace) {
    return null;
  }

  return {
    id: otherWorkspace.id,
    name: otherWorkspace.name,
    isPublic: otherWorkspace.type === 'PUBLIC',
    isPrivate: otherWorkspace.type === 'PRIVATE',
    type: otherWorkspace.type,
  };
}
